import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TaskColumn } from "@/features/tasks/types/task";
import {
  createColumn,
  updateColumn,
} from "@/features/tasks/services/taskColumnService";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

const COLOR_OPTIONS = [
  { value: "slate", label: "Slate", className: "bg-gray-400" },
  { value: "blue", label: "Blue", className: "bg-blue-500" },
  { value: "green", label: "Green", className: "bg-green-500" },
  { value: "amber", label: "Amber", className: "bg-amber-500" },
  { value: "red", label: "Red", className: "bg-red-500" },
  { value: "purple", label: "Purple", className: "bg-purple-500" },
  { value: "pink", label: "Pink", className: "bg-pink-500" },
  { value: "indigo", label: "Indigo", className: "bg-indigo-500" },
];

interface ColumnFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError?: (err: any) => void;
  column?: TaskColumn | null;
  nextOrder: number;
  ownerType?: "user" | "team";
  ownerId?: string;
}

export const ColumnFormModal: React.FC<ColumnFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  onError,
  column,
  nextOrder,
  ownerType = "user",
  ownerId,
}) => {
  const { t } = useTranslation();
  const isEdit = !!column;

  const [label, setLabel] = useState("");
  const [color, setColor] = useState("blue");
  const [labelError, setLabelError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLabel(column?.label ?? "");
      setColor(column?.color ?? "blue");
      setLabelError("");
    }
  }, [isOpen, column]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      setLabelError(t("common.required", { defaultValue: "This field is required" }));
      return;
    }
    setLabelError("");
    setSaving(true);
    try {
      if (isEdit && column) {
        await updateColumn(column.id, { label: label.trim(), color }, ownerType, ownerId);
      } else {
        await createColumn({ label: label.trim(), color, col_order: nextOrder }, ownerType, ownerId);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Failed to save column", err);
      onError?.(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t("tasks.column.edit_title") : t("tasks.column.new_title")}
      size="md"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          label={t("tasks.column.field_label")}
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            if (e.target.value.trim()) setLabelError("");
          }}
          placeholder={t("tasks.column.field_label")}
          error={labelError}
          required
        />

        {/* Color picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("tasks.column.field_color")}
          </label>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setColor(opt.value)}
                className={`w-8 h-8 rounded-full transition-all ${opt.className} ${
                  color === opt.value
                    ? "ring-2 ring-offset-2 ring-gray-600 scale-110"
                    : "hover:scale-105"
                }`}
                title={opt.label}
              />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1 capitalize">
            {COLOR_OPTIONS.find((o) => o.value === color)?.label}
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            {t("tasks.form.cancel")}
          </Button>
          <Button type="submit" loading={saving}>
            {saving
              ? t("tasks.form.saving")
              : isEdit
              ? t("tasks.form.save")
              : t("tasks.new_column")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
